import './styles/root/app.scss';
import { Navigate, Route, useLocation, useNavigate } from 'react-router-dom';
import { Routes } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import history from './history';
import { fetchAppInit } from './lib/api/app-init';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from './store/store';
import {
  setOnboardingOAuthCallbackParams,
  setPreUserInvitation,
  setReferral,
  updateIntercom,
} from './store/reducers/userSlice';
import { AnimatePresence } from 'framer-motion';
import Message from './components/generic/Message';
import PageNotFound from './pages/pageNotFound/PageNotFound';
import EmailVerificationSuccess from './pages/redirects/EmailVerificationSuccess';
import VerifyEmail from './pages/redirects/VerifyEmail';
import StripeSubscriptionSuccess from './pages/redirects/StripeSubscriptionSuccess';
import StripeSubscriptionFailed from './pages/redirects/StripeSubscriptionFailed';
import i18n from './internationalization/i18n';
import AdUpPayOnboardingSuccess from './pages/redirects/AdUpPayOnboardingSuccess';
import OnboardingCompleteBanner from './ui/banners/OnboardingCompleteBanner';
import appThemeEngine from './lib/helper-functions/appThemeEngine';
import moment from 'moment';
import { tokenRevokeAtLogOutUser } from './lib/api/logout-user';
import { setMessage } from './store/reducers/appSlice';
import { useTranslation } from 'react-i18next';
import PluginAuthPage from './pages/plugin/PluginAuthPage';
import WhiteLabel from './pages/whiteLabel/WhiteLabel';
import {
  setAutoSelectAgencyRole,
  setIsOnboardingCompleteBannerOpen,
  setIsPaymentPopupOpen,
  setIsPaymentPopupFromBrandLimit,
  setIsSwitchShopPopupOpen,
  setIsWhiteLabel,
  setIsHelpSetupPopupOpen,
  setIsSidebarMinimized,
  setIsChatAnimTriggered,
} from './store/reducers/uiSlice';
import Unsubscribe from './pages/redirects/Unsubscribe';
import SettingsPage from './pages/settings/SettingsPage';
import React from 'react';
import PortalLogin from './pages/login-via-portal/PortalLogin';
import CheckoutTrackingPage from './pages/checkoutTracking/CheckoutTrackingPage';
import Dashboard from './pages/dashboard/Dashboard';
import HeaderMain from './components/header/HeaderMain';
import { runCacheBuster } from './lib/cache-buster/cache-bust-handler';
import {
  APP_ENV,
  CACHE_BUSTER_EXECUTION_INTERVAL,
  GOOGLE_OAUTH_CLIENT_ID,
  INTERCOM_APP_ID,
  INTERCOM_ENABLED_ENVS,
  REDUX_STORE_RESYNC_INTERVAL,
} from './lib/constants/central-api';
import isNotEmptyString from './lib/helper-functions/isNotEmptyString';
import { allRoutes, HOME_ROUTE } from './lib/constants/route';
import RecommendationsSubscriptionStatus from './pages/redirects/RecommendationsSubscriptionStatus';
import { GoogleOAuthProvider } from '@react-oauth/google';
import localStorageHardReset from './lib/helper-functions/localStorageHardReset';
import get_access_token_by_integration_key_api_call from './lib/api/ai-tracker/get_access_token_by_integration_key_api_call';
import Tara from './pages/tara/Tara';
import SplashScreenNew from './components/splash-screen/SplashScreenNew';
import Popup from './ui/popup/Popup';
import get_tara_packages_api_call from './lib/api/tara/get_tara_packages_api_call';
import Recommendations from './pages/recommendations/Recommendations';
import Notifications from './pages/Notifications/Notifications';
import Integrations from './pages/integrations/Integrations';
import SwitchShop from './components/sidebar/SwitchShop';
import AIOptimizations from './pages/AIOptimizations/AIOptimizations';
import ActionsPage from './pages/actions/ActionsPage';
import ClientsPage from './pages/clients/ClientsPage';
import {
  setCurrentChatInfo,
  setLastUsedMCPs,
} from './store/reducers/chatSlice';
import SubscriptionPackages from './components/SubscriptionPackages/SubscriptionPackages';
import { requestPreUserInvitation } from './lib/api/pre-user-invitation';
import taraChatAnimationEngine from './lib/helper-functions/taraChatAnimationEngine';
import MainAuthWrapper from './onboardingFlow/MainAuthWrapper';
import Onboarding from './onboardingFlow/onboarding/Onboarding';
import AgencyPackageItem from './components/SubscriptionPackages/agency/AgencyPackageItem';
import SellerPackageItem from './components/SubscriptionPackages/seller/SellerPackageItem';
import {
  tara_agency_pricing_hierarchy,
  tara_seller_pricing_hierarchy,
} from './lib/constants/tara_pricing_plans';

function App() {
  const { t } = useTranslation();
  const location = useLocation();
  const currentRoute = location.pathname;
  const queryParameters = new URLSearchParams(window.location.search);

  const [isSidebarDrawerOpen, setIsSidebarDrawerOpen] =
    useState<boolean>(false);
  const [isShopSwitching, setIsShopSwitching] = useState<boolean>(false);
  const currentChat = useSelector((state: RootState) => state.chat.currentChat);
  const isPaymentPopupOpen = useSelector(
    (state: RootState) => state.UI.isPaymentPopupOpen
  );
  const isPaymentPopupFromBrandLimit = useSelector(
    (state: RootState) => state.UI.isPaymentPopupFromBrandLimit
  );
  const isSwitchShopPopupOpen = useSelector(
    (state: RootState) => state.UI.isSwitchShopPopupOpen
  );
  const isHelpSetupPopupOpen = useSelector(
    (state: RootState) => state.UI.isHelpSetupPopupOpen
  );
  const hasSkippedHelpSetup = useSelector(
    (state: RootState) => state.UI.hasSkippedHelpSetup
  );

  const toggleSidebarDrawer = () => {
    setIsSidebarDrawerOpen(!isSidebarDrawerOpen);
  };
  const togglePaymentPopupModal = () => {
    const newState = !isPaymentPopupOpen;
    dispatch(setIsPaymentPopupOpen(newState));
    // Show change plan view only if opened from brand limit AND has active subscription
    if (newState) {
      setShowChangePlanView(
        isPaymentPopupFromBrandLimit && !!taraSubscriptionDetails
      );
    } else {
      setShowChangePlanView(false);
      // Reset the brand limit flag when closing
      dispatch(setIsPaymentPopupFromBrandLimit(false));
    }
  };
  const toggleSwitchShopPopupModal = () => {
    if (isShopSwitching) return; //? Prevent closing popup when switching shop
    dispatch(setIsSwitchShopPopupOpen(!isSwitchShopPopupOpen));
  };

  /* get onboarding data */
  const staticURL = useSelector((state: RootState) => state.app.staticURL);
  const onboardingObject = useSelector(
    (state: RootState) => state.user.onboarding.onboarding_object
  );
  const userProfile = useSelector((state: RootState) => state.user.profile);
  const merchantTheme = useSelector(
    (state: RootState) => state.user?.merchant?.config
  );
  const intercomData = useSelector((state: RootState) => state.user.intercom);
  const accessToken = useSelector(
    (state: RootState) => state.auth.authorization.accessToken
  );
  const isAdminSession = useSelector(
    (state: RootState) => state.auth.authorization?.isAdminSession ?? false
  );
  const subscriptionLock = useSelector(
    (state: RootState) => state.auth.authorization.subscriptionLock
  );
  const message = useSelector((state: RootState) => state.app.message);
  const isInitSuccess = useSelector(
    (state: RootState) => state.app.isInitSuccess
  );
  const appLanguage = useSelector((state: RootState) => state.app.language);
  const isAppThemeDark = useSelector((state: RootState) => state.UI.theme.dark);
  const appThemeType = useSelector((state: RootState) => state.UI.theme.type);
  const {
    isTaraChatAnimationEnabled,
    isSidebarMinimized,
    isChatAnimTriggered,
  } = useSelector((s: RootState) => s.UI);
  const isWhiteLabel = useSelector((state: RootState) => state.UI.isWhiteLabel);
  const isOnboardingCompleteBannerOpen = useSelector(
    (state: RootState) => state.UI.isOnboardingCompleteBannerOpen
  );
  const lastVersionCheckTimestamp = useSelector(
    (state: RootState) => state.app.lastVersionCheckTimestamp ?? 0
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [taraSubscriptionPackages, setTaraSubscriptionPackages] = useState<
    any[]
  >([]);
  const [showChangePlanView, setShowChangePlanView] = useState<boolean>(false);

  const is_ai_onboarding_inprogress = useSelector(
    (state: RootState) => state.user?.ai_onboarding?.is_ai_onboarding_inprogress
  );
  const is_ai_onboarding_complete = useSelector(
    (state: RootState) => state.user?.ai_onboarding?.is_ai_onboarding_completed
  );
  const is_tracker_classes_configured = useSelector(
    (state: RootState) => state.user?.stats?.is_tracker_classes_configured
  );
  const ai_tracker_api_key = useSelector(
    (state: RootState) => state.user?.stats?.ai_tracker_api_key
  );
  const is_tracker_access_token = useSelector(
    (state: RootState) => state.user?.stats?.ai_tracker_access_token
  );
  const ai_tracker_access_token_expires_in = useSelector(
    (state: RootState) => state.user?.stats?.ai_tracker_access_token_expires_in
  );
  const shopSlug = useSelector(
    (state: RootState) => state.user.company?.shop_slug
  );
  const availableTaraSubscriptionPackages = useSelector(
    (state: RootState) => state.user?.shop?.availableTaraSubscriptionPackages
  );
  const taraSubscriptionDetails: any = useSelector(
    (state: RootState) => state.user?.shop?.taraSubscriptionDetails
  );
  const userRole = useSelector((state: RootState) => state.user?.profile?.role);

  // Update showChangePlanView when subscription details change or popup opens from brand limit
  useEffect(() => {
    if (isPaymentPopupOpen) {
      // Only show change plan view if opened from brand limit AND has active subscription
      setShowChangePlanView(
        isPaymentPopupFromBrandLimit && !!taraSubscriptionDetails
      );
    }
  }, [
    taraSubscriptionDetails,
    isPaymentPopupOpen,
    isPaymentPopupFromBrandLimit,
  ]);

  const is_ai_onboarding_completed = useSelector(
    (state: RootState) => state.user?.ai_onboarding?.is_ai_onboarding_completed
  );
  const isTrackingScriptConnectionSuccess = useSelector(
    (state: RootState) =>
      state.user?.isTrackingScriptConnectionSuccess ?? undefined
  );

  const [statistics, setStatistics] = useState<any>(null);

  const [appLoading, setAppLoading] = useState<boolean>(true);

  // useEffect(() => {
  //   // On initial app load, we need to fetch statistics and integration data.
  //   // On every page load for the dashboard route ('/dashboard'), we need to fetch statistics.

  //   const fetchStatistics = async () => {
  //     if (
  //       accessToken?.sellerToken &&
  //       shopSlug &&
  //       (currentRoute === '/dashboard' ||
  //         currentRoute !== '/recommendations' ||
  //         currentRoute === undefined) // Check if we are on the dashboard or initial load
  //     ) {
  //       try {
  //         const res = await get_statistics_api_call();
  //         get_available_tracking_data_api_call();
  //         setStatistics(res); // Set the fetched statistics to state
  //       } catch (error) {
  //         console.error('Error fetching statistics:', error);
  //       }
  //     }
  //   };

  //   fetchStatistics();
  // }, [accessToken?.sellerToken, shopSlug, currentRoute]);

  // useEffect(() => {
  //   //* Fetch Tracking Script connection status at initial user login
  //   if (
  //     (!isNotEmptyString(isTrackingScriptConnectionSuccess) ||
  //       currentRoute === '/integrations') &&
  //     isNotEmptyString(accessToken?.sellerToken) &&
  //     isNotEmptyString(shopSlug) &&
  //     currentRoute !== '/dashboard' &&
  //     currentRoute !== '/recommendations'
  //   ) {
  //     get_available_tracking_data_api_call(true); //checkScriptConnection
  //   }
  // }, [accessToken?.sellerToken, shopSlug, currentRoute]);

  const handlePreUserInvitation = async (invitationId: string) => {
    try {
      const response = await requestPreUserInvitation(invitationId);
      const userData = response?.data?.user_data;
      if (response && response?.success && userData) {
        dispatch(setPreUserInvitation({ id: invitationId, data: userData }));
      }
    } catch (error) {
      console.error('Error handling pre-user invitation:', error);
    }
  };

  useEffect(() => {
    if (
      ai_tracker_api_key &&
      typeof ai_tracker_api_key === 'string' &&
      is_tracker_access_token === ''
    ) {
      get_access_token_by_integration_key_api_call(ai_tracker_api_key);
    } else if (
      ai_tracker_access_token_expires_in &&
      moment(ai_tracker_access_token_expires_in) < moment()
    ) {
      get_access_token_by_integration_key_api_call(ai_tracker_api_key);
    }
  }, [
    ai_tracker_api_key,
    is_tracker_access_token,
    ai_tracker_access_token_expires_in,
    currentRoute,
  ]);

  const taraSubscriptionPackagesAPICall = async () => {
    try {
      const shouldSyncPackages = () => {
        if (
          availableTaraSubscriptionPackages?.syncTS &&
          availableTaraSubscriptionPackages?.structuredPackageData
        ) {
          const currentTime: number = new Date().getTime();
          const timeDiff: number =
            currentTime - availableTaraSubscriptionPackages?.syncTS;

          //? Check if the last sync was less than REDUX_STORE_RESYNC_INTERVAL 👇🏼
          if (timeDiff < REDUX_STORE_RESYNC_INTERVAL) {
            return false;
          }
        }
        return true;
      };

      const structuredPackageData = shouldSyncPackages()
        ? await get_tara_packages_api_call()
        : availableTaraSubscriptionPackages?.structuredPackageData;

      setTaraSubscriptionPackages(structuredPackageData);
    } catch (error: any) {
      console.error('Error fetching tara packages:', error);
    }
  };

  useLayoutEffect(() => {
    // only when we're on onboarding
    if (!window.location.pathname.startsWith('/onboarding')) return;

    const search = window.location.search; // e.g. "?code=...&state=..."
    if (!search) return;

    const p = new URLSearchParams(search);
    const code = p.get('code') || p.get('auth_code');
    const state = p.get('state');

    if (code && state) {
      // store the whole query so rerenders/redirects don't drop it
      dispatch(setOnboardingOAuthCallbackParams(search));
      // clean the URL (optional)
      window.history.replaceState({}, '', window.location.pathname);
      console.log('[OAuth][App] captured for onboarding');
    }
  }, [dispatch, location.pathname]);

  useEffect(() => {
    if (
      accessToken?.sellerToken &&
      !is_ai_onboarding_inprogress &&
      shopSlug &&
      isInitSuccess &&
      !appLoading
    ) {
      taraSubscriptionPackagesAPICall();
    }
  }, [
    accessToken?.sellerToken,
    shopSlug,
    is_ai_onboarding_inprogress,
    isInitSuccess,
    appLoading,
  ]);

  useEffect(() => {
    if (accessToken?.sellerToken) {
      const shouldRedirect =
        currentRoute !== '/register' &&
        currentRoute !== '/login-via-portal' &&
        currentRoute !== '/verify-email' &&
        !isAdminSession;

      //* Commented below for the MCP Launch
      // if (
      //   shouldRedirect &&
      //   !is_ai_onboarding_complete &&
      //   !is_tracker_classes_configured
      // ) {
      //   navigate('/onboarding', { replace: true });
      // } else if (shouldRedirect && is_ai_onboarding_inprogress) {
      //   navigate('/onboarding', { replace: true });
      // }
      //* Added below for the MCP Launch
      if (shouldRedirect && is_ai_onboarding_inprogress) {
        navigate('/onboarding', { replace: true });
      }
    }
  }, [
    currentRoute,
    isAdminSession,
    accessToken?.sellerToken,
    is_ai_onboarding_inprogress,
    is_ai_onboarding_complete,
    is_tracker_classes_configured,
  ]);

  useEffect(() => {
    if (currentRoute === '/tara') {
      dispatch(
        setCurrentChatInfo({
          chatTokenCount: 0,
        })
      );
      dispatch(setLastUsedMCPs([]));
    }
  }, [currentRoute]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(setIsOnboardingCompleteBannerOpen(false));
      clearTimeout(timer);
    }, 4000);
  }, [isOnboardingCompleteBannerOpen, dispatch]);

  useLayoutEffect(() => {
    const unlisten = history.listen(({ location, action }) => {
      let unblock = history.block((tx) => {
        // Navigation was blocked! Let's show a confirmation dialog
        // so the user can decide if they actually want to navigate
        // away and discard changes they've made in the current page.
        let url = tx.location.pathname;
        if (window.confirm(`Are you sure you want to go to ${url}?`)) {
          // Unblock the navigation.
          unblock();

          // Retry the transition.
          tx.retry();
        }
      });
    });

    return unlisten;
  }, []);

  //* Changing app language
  useEffect(() => {
    i18n.changeLanguage(appLanguage);
  }, [appLanguage]);

  //* Set auto select agency role based on URL params
  useEffect(() => {
    const accType = queryParameters.get('acctype') as string;
    if (accType && accType === 'agency') {
      dispatch(setAutoSelectAgencyRole(true));
    } else if (accType && (accType === 'seller' || accType === 'individual')) {
      dispatch(setAutoSelectAgencyRole(false));
    }
  }, [currentRoute, queryParameters]);

  //* Fetch app init
  useEffect(() => {
    if (currentRoute !== '/authorize') {
      dispatch(setIsWhiteLabel(false));
    }
    if (
      currentRoute !== '/authorize' &&
      currentRoute !== '/login-via-portal' &&
      currentRoute !== '/register' &&
      currentRoute !== '/login' &&
      currentRoute !== '/forgot-password' &&
      currentRoute !== '/reset-password'
    ) {
      fetchAppInit();
    }
  }, []);

  //* Extracting cacheBustToken from URL if there is any
  const cacheBustToken = queryParameters.get(
    'cacheBustToken'
  ) as unknown as string;

  //* Set referral & preUserInvite data via URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const referralParam = params.get('referral');
    const preUserInviteParam = params.get('preUserInvite');

    if (referralParam && typeof referralParam === 'string') {
      dispatch(setReferral(referralParam));
    }

    if (preUserInviteParam && typeof preUserInviteParam === 'string') {
      handlePreUserInvitation(preUserInviteParam);
    }
  }, []);

  //* Clean up cache bust token from URL after handling
  useEffect(() => {
    if (isNotEmptyString(cacheBustToken)) {
      const url = new URL(window.location.href);
      url.searchParams.delete('cacheBustToken');
      window.history.replaceState({}, '', url.toString());
      console.log('🧹 Cache bust token cleaned from URL');
    }
  }, [cacheBustToken]);

  //* Show help setup popup when user lands on HOME_ROUTE or integrations without contact number
  useEffect(() => {
    if (
      isInitSuccess &&
      !appLoading &&
      (currentRoute === HOME_ROUTE || currentRoute === '/integrations') &&
      !hasSkippedHelpSetup &&
      (!userProfile.phone || userProfile.phone.trim() === '')
    ) {
      dispatch(setIsHelpSetupPopupOpen(true));
    }
  }, [
    isInitSuccess,
    appLoading,
    currentRoute,
    hasSkippedHelpSetup,
    userProfile.phone,
  ]);

  //* ******************** Run Cache Buster (FE version check) at given interval --- START ******************** *//

  useEffect(() => {
    const interval = setInterval(() => {
      const currentTime: number = new Date().getTime();
      const hoursDifference: number =
        (currentTime - lastVersionCheckTimestamp) / (1000 * 60 * 60);

      //? Check if the last version check was more than 12 hours ago 👇🏼
      if (isInitSuccess && hoursDifference >= 12) {
        console.log(
          '🔄 CacheBuster : (FE version check) 12 hour interval reached ⏰'
        );
        runCacheBuster();
      }
    }, CACHE_BUSTER_EXECUTION_INTERVAL);

    return () => clearInterval(interval);
  }, [isInitSuccess]);
  //* ******************** Run Cache Buster (FE version check) at given interval --- END ******************** *//

  /* --------------Token expiry check Start---------------- */
  useEffect(() => {
    if (!isNotEmptyString(cacheBustToken)) {
      console.log(
        '⏰ 🚧 Current access token will be expired on :',
        accessToken?.expiresOn
      );
      if (
        currentRoute !== '/authorize' &&
        currentRoute !== '/forgot-password' &&
        currentRoute !== '/reset-password' &&
        currentRoute !== '/login-via-portal' &&
        currentRoute !== '/forgot-password' &&
        currentRoute !== '/reset-password' &&
        currentRoute !== '/register' &&
        currentRoute !== '/login'
      ) {
        if (accessToken?.expiresOn && accessToken?.expiresOn !== null) {
          if (moment(accessToken?.expiresOn) < moment()) {
            console.log('❌ Access token expired. Logging Out user');
            logoutHandler();
            dispatch(
              setMessage({
                message: t('SessionExpired'),
                messageType: 'error',
              })
            );
          } else {
            console.log('✅ Access token still active');
          }
        } else {
          logoutHandler();
        }
      }
    } else {
      //* Handle cache bust scenario - if we have cacheBustToken but no valid token, redirect to login
      if (!accessToken?.sellerToken && currentRoute === '/') {
        console.log(
          '🔄 Cache bust detected with no valid token, redirecting to login'
        );
        navigate('/login', { replace: true });
      }
    }
  }, [accessToken?.expiresOn, cacheBustToken, currentRoute]);
  /* --------------Token expiry check End---------------- */

  /* --------------App Theme Settings Start---------------- */
  useEffect(() => {
    appThemeEngine(isAppThemeDark, merchantTheme, appThemeType);
  }, [isAppThemeDark, merchantTheme]);
  /* --------------App Theme Settings End------------------ */

  /* --------------Setting Favicon Start---------------- */
  useEffect(() => {
    const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;

    if (staticURL && merchantTheme?.merchant_favicon) {
      link.href = `${staticURL}/medium/${merchantTheme.merchant_favicon}`;
      console.log('has merchant favicon');
    } else {
      link.href = '/favicon.ico'; // Assuming the favicon is located in the public folder
      console.log('no merchant favicon');
    }
  }, [staticURL, merchantTheme]);
  /* --------------Setting Favicon End------------------ */

  /* -------------run intercom at app load--------------- */
  useEffect(() => {
    if (
      // @ts-ignore:next-line
      window.Intercom &&
      INTERCOM_ENABLED_ENVS.includes(APP_ENV)
    ) {
      const appId = intercomData.app_id || INTERCOM_APP_ID;

      if (!intercomData.isFirstBootComplete) {
        console.log('intercom first boot running...');
        // @ts-ignore:next-line
        window.Intercom('boot', {
          app_id: appId,
          name: `${userProfile?.first_name} ${userProfile?.last_name}`,
          email: `${userProfile?.email}`,
          phone: `+${userProfile?.country_code}${userProfile?.phone}`,
          api_base: 'https://api-iam.intercom.io',
          hide_default_launcher:
            // !accessToken?.sellerToken ||
            currentRoute === '/onboarding' ||
            currentRoute === '/login' ||
            currentRoute === '/forgot-password' ||
            currentRoute === '/reset-password' ||
            // currentRoute === '/register' ||
            currentRoute === '/',
        });

        dispatch(
          updateIntercom({
            isFirstBootComplete: true,
          })
        );
      } else {
        // Update launcher visibility based on token availability & currentRoute
        // @ts-ignore:next-line
        window.Intercom('update', {
          hide_default_launcher:
            // !accessToken?.sellerToken ||
            currentRoute === '/onboarding' ||
            currentRoute === '/login' ||
            currentRoute === '/forgot-password' ||
            currentRoute === '/reset-password' ||
            // currentRoute === '/register' ||
            currentRoute === '/',
        });
      }

      // Only update user data if user is logged in
      if (accessToken?.sellerToken) {
        // @ts-ignore:next-line
        window.intercomSettings = {
          email: `${userProfile.email}`,
          app_id: appId,
          cms: intercomData.cms,
          cmsdatafilled: intercomData.cmsdatafilled,
          psp: intercomData.psp,
          pspdatafilled: intercomData.pspdatafilled,
        };

        console.log('intercom updating...');
        // @ts-ignore:next-line
        window.Intercom('update', {
          app_id: appId,
          name: `${userProfile.first_name} ${userProfile.last_name}`,
          email: `${userProfile.email}`,
          api_base: 'https://api-iam.intercom.io',
          hide_default_launcher:
            currentRoute === '/onboarding' ||
            currentRoute === '/login' ||
            currentRoute === '/forgot-password' ||
            currentRoute === '/reset-password' ||
            // currentRoute === '/register' ||
            currentRoute === '/',
        });
      }
    }
    // @ts-ignore:next-line
    /* console.log(window.intercomSettings); */
  }, [
    APP_ENV,
    userProfile,
    accessToken?.sellerToken,
    currentRoute,
    intercomData,
    dispatch,
  ]);
  /* -------------run intercom when loggedin user data loads code end--------------- */

  const logoutHandler = () => {
    tokenRevokeAtLogOutUser();
    if (INTERCOM_ENABLED_ENVS.includes(APP_ENV)) {
      // @ts-ignore:next-line
      window.Intercom && window.Intercom('shutdown'); //shutdown intercom
    }

    localStorageHardReset();

    if (isWhiteLabel) {
      navigate('/authorize');
    } else {
      //* Explicitly handle base URL case to prevent getting stuck
      const currentPath = window?.location?.pathname;
      if (currentPath === '/' || currentPath === '/register') {
        navigate(currentPath === '/register' ? '/register' : '/login', {
          replace: true,
        });
      } else {
        navigate('/login', { replace: true });
      }
      fetchAppInit();
    }
  };

  const renderHeader = () => {
    if (window?.location?.pathname === '/onboarding') {
      return null;
    }
    if (accessToken?.sellerToken || isNotEmptyString(cacheBustToken)) {
      if (isInitSuccess && !isOnboardingCompleteBannerOpen && !appLoading) {
        return (
          <HeaderMain
            isSidebarDrawerOpen={isSidebarDrawerOpen}
            toggleSidebarDrawer={toggleSidebarDrawer}
          />
        );
      }
    }

    return null;
  };

  /*   --------------------- Tara Chat Animation Start --------------------- */
  const [hoveredNearHeader, setHoveredNearHeader] = useState(false);

  // Desktop watcher (≥ 801px)
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(min-width: 801px)').matches
      : true
  );

  // ---- keep latest Redux values in refs (fix stale closures) ----
  const isSidebarMinimizedRef = useRef(isSidebarMinimized);
  const isChatAnimTriggeredRef = useRef(isChatAnimTriggered);
  useEffect(() => {
    isSidebarMinimizedRef.current = isSidebarMinimized;
  }, [isSidebarMinimized]);
  useEffect(() => {
    isChatAnimTriggeredRef.current = isChatAnimTriggered;
  }, [isChatAnimTriggered]);

  // ---- media query effect (TS-safe, legacy-friendly) ----
  useEffect(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) return;
    const mql = window.matchMedia('(min-width: 801px)');

    setIsDesktop(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);

    if (typeof mql.addEventListener === 'function')
      mql.addEventListener('change', onChange);
    else (mql as any).addListener(onChange);

    return () => {
      if (typeof mql.removeEventListener === 'function')
        mql.removeEventListener('change', onChange);
      else (mql as any).removeListener(onChange);
    };
  }, []);

  // ---- main animation effect ----
  useEffect(() => {
    // Route gates
    const isSearchRoute = currentRoute.startsWith('/tara/search');
    const isChatRoute = currentRoute.startsWith('/tara');

    const removeStrip = () => {
      const stale = document.getElementById('tara-hover-reveal-strip');
      if (stale?.parentNode) stale.parentNode.removeChild(stale);
    };

    // If not on /tara/chat OR on /tara/search OR not desktop OR feature disabled:
    // force INITIAL, clean up, and clear the trigger
    if (
      !isChatRoute ||
      isSearchRoute ||
      !isDesktop ||
      !isTaraChatAnimationEnabled
    ) {
      taraChatAnimationEngine('initial', isSidebarMinimizedRef.current);
      setHoveredNearHeader(false);
      dispatch(setIsSidebarMinimized(false));
      dispatch(setIsChatAnimTriggered(false));
      removeStrip();
      return;
    }

    // If the animation hasn't been triggered yet, stay INITIAL (no compact, no hover logic)
    if (!isChatAnimTriggeredRef.current) {
      taraChatAnimationEngine('initial', isSidebarMinimizedRef.current);
      setHoveredNearHeader(false);
      dispatch(setIsSidebarMinimized(false));
      removeStrip();
      return;
    }

    // --- Desktop + /tara/chat + feature enabled + TRIGGERED ---

    // Baseline: compact after a short delay
    // Use actual state value instead of ref to ensure correct sidebar state
    const baselineTimer = window.setTimeout(() => {
      taraChatAnimationEngine('compact', isSidebarMinimized);
    }, 500);

    // Ensure a hover strip exists (top of viewport)
    let strip = document.getElementById(
      'tara-hover-reveal-strip'
    ) as HTMLElement | null;
    let createdStrip = false;
    if (!strip) {
      strip = document.createElement('div');
      strip.id = 'tara-hover-reveal-strip';
      Object.assign(strip.style, {
        position: 'fixed',
        top: '0px',
        left: '0px',
        right: '0px',
        height: '32px',
        zIndex: '1', // BELOW header (header is 2)
        pointerEvents: 'auto',
      });
      document.body.appendChild(strip);
      createdStrip = true;
    }

    // Header listeners: attach when header exists (covers mount-from-other-route case)
    let boundHeader: HTMLElement | null = null;
    let headerObserver: MutationObserver | null = null;

    const disableStrip = () => {
      if (strip) strip.style.pointerEvents = 'none';
    };
    const enableStrip = () => {
      if (strip) strip.style.pointerEvents = 'auto';
    };

    const onHeaderEnter = () => {
      cancelCompact();
      isHoveringHeader = true;
      setHoveredNearHeader(true);
      taraChatAnimationEngine('headerOnly', isSidebarMinimizedRef.current);
      // strip stays disabled while header is visible
      disableStrip();
    };
    const onHeaderLeave = () => {
      isHoveringHeader = false;
      enableStrip();
      scheduleCompact();
    };

    const bindHeader = (hdr: HTMLElement) => {
      if (boundHeader) return; // already bound
      boundHeader = hdr;
      hdr.addEventListener('mouseenter', onHeaderEnter);
      hdr.addEventListener('mouseleave', onHeaderLeave);
    };

    const tryBindHeaderNow = () => {
      const hdr = document.getElementById(
        'tara-header-bar'
      ) as HTMLElement | null;
      if (hdr) bindHeader(hdr);
      return !!hdr;
    };

    if (!tryBindHeaderNow()) {
      // Watch DOM for header mount, then bind once
      headerObserver = new MutationObserver(() => {
        if (tryBindHeaderNow()) {
          headerObserver?.disconnect();
          headerObserver = null;
        }
      });
      headerObserver.observe(document.body, { childList: true, subtree: true });
    }

    // Hover strip behavior
    let leaveTimer: number | null = null;
    let isHoveringHeader = false;

    const scheduleCompact = () => {
      if (leaveTimer) window.clearTimeout(leaveTimer);
      leaveTimer = window.setTimeout(() => {
        if (!isHoveringHeader) {
          setHoveredNearHeader(false);
          taraChatAnimationEngine('compact', isSidebarMinimizedRef.current);
          // Re-enable strip when compacting
          if (boundHeader) enableStrip();
        }
      }, 200) as unknown as number;
    };
    const cancelCompact = () => {
      if (leaveTimer) {
        window.clearTimeout(leaveTimer);
        leaveTimer = null;
      }
    };

    const onStripEnter = () => {
      cancelCompact();
      setHoveredNearHeader(true);
      taraChatAnimationEngine('headerOnly', isSidebarMinimizedRef.current);
    };
    const onStripLeave = () => {
      if (!isHoveringHeader) {
        scheduleCompact();
      }
    };

    strip.addEventListener('mouseenter', onStripEnter);
    strip.addEventListener('mouseleave', onStripLeave);

    const onScroll = () => {
      if (window.scrollY <= 2) onStripEnter();
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.clearTimeout(baselineTimer);
      strip?.removeEventListener('mouseenter', onStripEnter);
      strip?.removeEventListener('mouseleave', onStripLeave);
      if (boundHeader) {
        boundHeader.removeEventListener('mouseenter', onHeaderEnter);
        boundHeader.removeEventListener('mouseleave', onHeaderLeave);
      }
      if (headerObserver) {
        headerObserver.disconnect();
        headerObserver = null;
      }
      window.removeEventListener('scroll', onScroll);
      if (createdStrip && strip?.parentNode)
        strip.parentNode.removeChild(strip);
      if (leaveTimer) window.clearTimeout(leaveTimer);
    };
  }, [
    currentRoute, // re-run on route change
    isDesktop,
    isTaraChatAnimationEnabled,
    isChatAnimTriggered, // re-run when submit flips it to true
    isSidebarMinimized, // re-run when sidebar state changes
    dispatch,
  ]);

  // ---- keep compact layout in sync when the user toggles the sidebar ----
  useEffect(() => {
    const isSearchRoute = currentRoute.startsWith('/tara/search');
    const isChatRoute = currentRoute.startsWith('/tara');
    if (
      !isChatRoute ||
      isSearchRoute ||
      !isDesktop ||
      !isTaraChatAnimationEnabled
    )
      return;
    if (!isChatAnimTriggered) return;

    if (hoveredNearHeader) {
      taraChatAnimationEngine('headerOnly', isSidebarMinimized);
    } else {
      taraChatAnimationEngine('compact', isSidebarMinimized);
    }
  }, [
    isSidebarMinimized,
    isChatAnimTriggered,
    isDesktop,
    isTaraChatAnimationEnabled,
    currentRoute,
    hoveredNearHeader,
  ]);

  /*   --------------------- Tara Chat Animation End --------------------- */

  //* For better loading animation and prevent flickering
  useEffect(() => {
    const onPageLoad = () => {
      setTimeout(() => {
        setAppLoading(false);
      }, 1000);
    };
    if (document.readyState === 'complete') {
      onPageLoad();
    } else {
      window.addEventListener('load', onPageLoad, false);
      return () => window.removeEventListener('load', onPageLoad);
    }
  }, []);

  return (
    <div
      style={{
        backgroundColor:
          window.location.pathname === '/tara' ||
          window.location.pathname.startsWith('/tara/chat/')
            ? 'var(--sidebar-background-color)'
            : 'var(--main-background-color)',
      }}
      data-is-white-label={isWhiteLabel?.toString()}
      className='App'
    >
      {/* Onboarding Complete Banner */}
      <AnimatePresence>
        {message.messageType && (
          <Message msgType={message.messageType} msg={message.message} />
        )}
        {isOnboardingCompleteBannerOpen && <OnboardingCompleteBanner />}
      </AnimatePresence>

      {/* Pricing Plans Popup */}
      {isInitSuccess && !isOnboardingCompleteBannerOpen && !appLoading && (
        <div className='recommendations-payment-popup-custom'>
          <Popup
            styles={{
              backgroundColor: 'var(--sidebar-background-color)',
              width: showChangePlanView ? '90%' : undefined,
              maxWidth: showChangePlanView ? '1200px' : undefined,
              maxHeight: showChangePlanView ? '90vh' : undefined,
              overflow: showChangePlanView ? 'auto' : undefined,
            }}
            setTrigger={togglePaymentPopupModal}
            trigger={isPaymentPopupOpen}
          >
            {showChangePlanView && taraSubscriptionDetails ? (
              <div style={{ padding: '1rem' }}>
                <div
                  className={
                    userRole === 'agency' ? 'agencyPackages' : 'sellerPackages'
                  }
                  style={{
                    justifyItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                  }}
                >
                  {(userRole === 'agency' || userRole === 'seller') && (
                    <div
                      className={`${userRole}Packages__items`}
                      style={{
                        flexWrap: 'wrap',
                        justifyContent: 'space-evenly',
                      }}
                    >
                      {(() => {
                        const rolePackages =
                          availableTaraSubscriptionPackages?.structuredPackageData?.filter(
                            (pkg: any) => pkg.role === userRole
                          ) || [];

                        if (rolePackages.length === 0) return null;

                        const PackageItemComponent =
                          userRole === 'agency'
                            ? AgencyPackageItem
                            : SellerPackageItem;
                        const pricingHierarchy =
                          userRole === 'agency'
                            ? tara_agency_pricing_hierarchy
                            : tara_seller_pricing_hierarchy;

                        const activeItems: JSX.Element[] = [];
                        const upgradableItems: JSX.Element[] = [];
                        const disabledItems: JSX.Element[] = [];

                        rolePackages.forEach((pkg: any) => {
                          const monthlyIsActive =
                            taraSubscriptionDetails?.package_slug ===
                            pkg.monthly_slug;
                          const annualIsActive =
                            taraSubscriptionDetails?.package_slug ===
                            pkg.annual_slug;

                          const purchasedSeatCount =
                            (monthlyIsActive || annualIsActive) &&
                            taraSubscriptionDetails?.purchased_shops?.seat_count
                              ? parseInt(
                                  taraSubscriptionDetails.purchased_shops
                                    .seat_count
                                )
                              : undefined;

                          const monthlyIsUpgradable =
                            pricingHierarchy[
                              taraSubscriptionDetails?.package_slug as keyof typeof pricingHierarchy
                            ] <
                            pricingHierarchy[
                              pkg.monthly_slug as keyof typeof pricingHierarchy
                            ];

                          const annualIsUpgradable =
                            pricingHierarchy[
                              taraSubscriptionDetails?.package_slug as keyof typeof pricingHierarchy
                            ] <
                            pricingHierarchy[
                              pkg.annual_slug as keyof typeof pricingHierarchy
                            ];

                          const monthlyItem = (
                            <PackageItemComponent
                              key={`${pkg.id}-monthly`}
                              isMonthly={true}
                              isCancelMode={true}
                              purchased_seat_count={purchasedSeatCount}
                              packageDetails={{
                                ...pkg,
                                is_active: monthlyIsActive,
                                subscription_id: monthlyIsActive
                                  ? taraSubscriptionDetails?.id
                                  : undefined,
                                next_billing_date: monthlyIsActive
                                  ? taraSubscriptionDetails?.end_date
                                  : undefined,
                                is_most_popular: false,
                              }}
                              isUpgradable={monthlyIsUpgradable}
                            />
                          );

                          const annualItem = (
                            <PackageItemComponent
                              key={`${pkg.id}-annual`}
                              isMonthly={false}
                              isCancelMode={true}
                              purchased_seat_count={purchasedSeatCount}
                              packageDetails={{
                                ...pkg,
                                is_active: annualIsActive,
                                subscription_id: annualIsActive
                                  ? taraSubscriptionDetails?.id
                                  : undefined,
                                next_billing_date: annualIsActive
                                  ? taraSubscriptionDetails?.end_date
                                  : undefined,
                                is_most_popular: false,
                              }}
                              isUpgradable={annualIsUpgradable}
                            />
                          );

                          if (monthlyIsActive) {
                            activeItems.push(monthlyItem);
                          } else if (monthlyIsUpgradable) {
                            upgradableItems.push(monthlyItem);
                          } else {
                            disabledItems.push(monthlyItem);
                          }

                          if (annualIsActive) {
                            activeItems.push(annualItem);
                          } else if (annualIsUpgradable) {
                            upgradableItems.push(annualItem);
                          } else {
                            disabledItems.push(annualItem);
                          }
                        });

                        return [
                          ...activeItems,
                          ...upgradableItems,
                          ...disabledItems,
                        ];
                      })()}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <SubscriptionPackages
                taraSubscriptionPackages={taraSubscriptionPackages}
              />
            )}
          </Popup>
        </div>
      )}

      {/* Switch Shop Popup */}
      {isInitSuccess && !isOnboardingCompleteBannerOpen && !appLoading && (
        <div className='recommendations-payment-popup-custom'>
          <Popup
            styles={{
              backgroundColor: 'var(--sidebar-background-color)',
            }}
            setTrigger={toggleSwitchShopPopupModal}
            trigger={isSwitchShopPopupOpen}
            switching={isShopSwitching}
          >
            <SwitchShop onSwitchingChange={setIsShopSwitching} />
          </Popup>
        </div>
      )}

      {/* Help Setup Popup */}
      {/* Hidden for new test onboarding process */}
      {/*       {isInitSuccess && !isOnboardingCompleteBannerOpen && !appLoading && (
        <HelpSetupPopup
          isOpen={isHelpSetupPopupOpen}
          onClose={() => {
            dispatch(setHasSkippedHelpSetup(true));
            dispatch(setIsHelpSetupPopupOpen(false));
          }}
        />
      )} */}

      {/* Header */}
      {renderHeader()}

      {/* //* App Routes --- START */}
      <React.Fragment>
        {(!isInitSuccess || appLoading) &&
        window?.location?.pathname !== '/authorize' &&
        window?.location?.pathname !== '/login-via-portal' ? (
          <SplashScreenNew />
        ) : (
          <Routes>
            <Route
              path='/'
              element={
                isWhiteLabel ? (
                  <Navigate to={'/authorize'} />
                ) : (
                  <Navigate to={'/login'} />
                )
              }
            />
            <Route
              path='/register'
              element={
                !accessToken?.sellerToken && !isWhiteLabel ? (
                  <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
                    {/* {APP_ENV === 'DEV' ? <MainAuthWrapper /> : <SignUpPage />} */}
                    <MainAuthWrapper />
                  </GoogleOAuthProvider>
                ) : (
                  <Navigate to={'/onboarding'} />
                )
              }
            />
            <Route
              path='/login'
              element={
                !accessToken?.sellerToken && !isWhiteLabel ? (
                  <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
                    {/* {APP_ENV === 'DEV' ? <MainAuthWrapper /> : <LoginPage />} */}
                    <MainAuthWrapper />
                  </GoogleOAuthProvider>
                ) : !isWhiteLabel ? (
                  <Navigate to={HOME_ROUTE} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route
              path='/forgot-password'
              element={
                !accessToken?.sellerToken && !isWhiteLabel ? (
                  <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
                    {/* {APP_ENV === 'DEV' ? (
                      <MainAuthWrapper />
                    ) : (
                      <ForgotPassword />
                    )} */}
                    <MainAuthWrapper />
                  </GoogleOAuthProvider>
                ) : !isWhiteLabel ? (
                  <Navigate to={HOME_ROUTE} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route
              path='/reset-password'
              element={
                !accessToken?.sellerToken && !isWhiteLabel ? (
                  <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
                    {/* {APP_ENV === 'DEV' ? (
                      <MainAuthWrapper />
                    ) : (
                      <ResetPassword />
                    )} */}
                    <MainAuthWrapper />
                  </GoogleOAuthProvider>
                ) : !isWhiteLabel ? (
                  <Navigate to={HOME_ROUTE} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route
              path='/onboarding/*'
              element={
                accessToken?.sellerToken ||
                accessToken?.currentWhiteLabelSellerToken ||
                isNotEmptyString(cacheBustToken) ? (
                  // <Onboarding />
                  // <AIOnboarding />
                  // APP_ENV === 'DEV' ? (
                  //   <Onboarding />
                  // ) : (
                  //   <TestOnboarding1 />
                  // )
                  <Onboarding />
                ) : !isWhiteLabel ? (
                  <Navigate to={'/login'} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            {allRoutes['/dashboard'] && (
              <Route
                path='/dashboard/'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ? (
                    isTrackingScriptConnectionSuccess &&
                    isTrackingScriptConnectionSuccess === 'true' ? (
                      <Dashboard statistics={statistics} />
                    ) : (
                      <Navigate to={'/integrations'} />
                    )
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/tara'] && (
              <Route
                path='/tara/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ? (
                    <Tara />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/reports'] && (
              <Route
                path='/reports/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ? (
                    <Notifications />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/ai-optimizations'] && (
              <Route
                path='/ai-optimizations/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ? (
                    <AIOptimizations />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/actions'] && (
              <Route
                path='/actions/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ? (
                    <ActionsPage />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/settings'] && (
              <Route
                path='/settings/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ? (
                    <SettingsPage />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/clients'] && (
              <Route
                path='/clients/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ? (
                    <ClientsPage />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/integrations'] && (
              <Route
                path='/integrations/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ? (
                    <Integrations />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/tracking'] && (
              <Route
                path='/tracking/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ? (
                    <CheckoutTrackingPage />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/recommendations'] && (
              <Route path='/recommendations/*' element={<Recommendations />} />
            )}
            <Route
              path='/plugin-connect'
              element={
                subscriptionLock && subscriptionLock === 'true' ? (
                  <Navigate to={'/settings/subscription'} />
                ) : accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ? (
                  <PluginAuthPage />
                ) : !isWhiteLabel ? (
                  <Navigate to={'/login'} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route
              path='/email-verification-success'
              element={<EmailVerificationSuccess />}
            />
            <Route path='/verify-email' element={<VerifyEmail />} />
            <Route path='/unsubscribe' element={<Unsubscribe />} />
            <Route
              path='/stripe-subscription-success'
              element={
                accessToken?.sellerToken || isNotEmptyString(cacheBustToken) ? (
                  <StripeSubscriptionSuccess />
                ) : !isWhiteLabel ? (
                  <Navigate to={'/login'} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route
              path='/stripe-subscription-failed'
              element={
                accessToken?.sellerToken || isNotEmptyString(cacheBustToken) ? (
                  <StripeSubscriptionFailed />
                ) : !isWhiteLabel ? (
                  <Navigate to={'/login'} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route
              path='/recommendations-subscription-status'
              element={
                accessToken?.sellerToken || isNotEmptyString(cacheBustToken) ? (
                  <RecommendationsSubscriptionStatus />
                ) : !isWhiteLabel ? (
                  <Navigate to={'/login'} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route
              path='/aduppay/onboarding'
              element={
                accessToken?.sellerToken || isNotEmptyString(cacheBustToken) ? (
                  <AdUpPayOnboardingSuccess />
                ) : !isWhiteLabel ? (
                  <Navigate to={'/login'} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route path='/authorize' element={<WhiteLabel />} />
            <Route path='/login-via-portal' element={<PortalLogin />} />
            <Route path='*' element={<PageNotFound />} />
          </Routes>
        )}
      </React.Fragment>
    </div>
  );
}

export default App;
